// Features: M004-F0803
// Tests: T-M004-0456 (apply, paging, watermark, removed, resnapshot, room check), T-M004-0457 (display text and sort)
// WebUI #267 (M004-F0803, design section 9): the pure model of the Mycelium room device list
// (Spec mycelium-room-device-list 1.0.1, list type mycelium.room_device). No React, no network.
// Rules applied here: ROOMLIST-RULE-004 (adopt high_watermark only from the page whose next_cursor is null),
// -005 (snapshot_required and incarnation change lead to a re-snapshot), -011 (room_uuid echo), -012 (removed[]),
// -013 (unknown kind renders as other), -014 (value types per kind).

export const KINDS = Object.freeze([
  'room_state', 'target_temperature', 'temperature', 'humidity', 'motion', 'occupancy', 'contact', 'climate',
  'select', 'switch', 'light', 'cover', 'sensor', 'binary_sensor', 'other',
]);

// Display groups, in order. Slot kinds (room state, target, room temperature) lead.
const GROUP_ORDER = Object.freeze([
  'room_state', 'target_temperature', 'temperature', 'humidity', 'motion', 'occupancy', 'contact',
  'binary_sensor', 'sensor', 'light', 'switch', 'climate', 'cover', 'select', 'other',
]);

export const NOT_CONNECTED_WORD = 'Not connected';
export const UNAVAILABLE_WORD = 'Unavailable';
export const NO_VALUE_WORD = 'No value';

export class RoomListError extends Error {
  constructor(failure) { super(failure); this.name = 'RoomListError'; this.listFailure = failure; }
}

export const createListState = () => ({ items: new Map(), watermark: null, incarnation: null });

export const normaliseKind = (kind) => (KINDS.includes(kind) ? kind : 'other');

const normaliseItem = (raw) => {
  if (!raw || typeof raw.device_key !== 'string' || !raw.device_key) return null;
  return {
    device_key: raw.device_key,
    kind: normaliseKind(raw.kind),
    label: typeof raw.label === 'string' ? raw.label : '',
    value: raw.value === undefined ? null : raw.value,
    unit: typeof raw.unit === 'string' && raw.unit ? raw.unit : null,
    observed_at: typeof raw.observed_at === 'string' ? raw.observed_at : null,
    state: raw.state === 'connected' || raw.state === 'unavailable' ? raw.state : 'not_connected',
  };
};

// ROOMLIST-RULE-011: a reply whose room_uuid differs from the request is refused. The browser does not know the room uuid (Core
// injects it), so the first reply pins it for this panel and every later reply must echo it; a known uuid is checked from the start.
export const assertRoomUuid = (page, expected) => {
  const got = page && page.room_uuid;
  if (typeof got !== 'string' || !got) throw new RoomListError('mismatch');
  if (expected && got !== expected) throw new RoomListError('mismatch');
  return got;
};

const isFinal = (page) => page.next_cursor === null || page.next_cursor === undefined;

// pages: the pages of one walk, in order. Returns { state, outcome }:
//   ok          the walk is complete; state replaced (snapshot) or merged (changes)
//   resnapshot  snapshot_required, an incarnation change, or changes with nothing to be relative to; state unchanged
//   incomplete  the walk was interrupted (last page still has a cursor); state unchanged, watermark and incarnation kept
export const applyPages = (prev, mode, pages) => {
  if (!Array.isArray(pages) || pages.length === 0) return { state: prev, outcome: 'incomplete' };
  if (mode === 'changes' && (prev.watermark === null || prev.incarnation === null)) return { state: prev, outcome: 'resnapshot' };
  const incarnation = pages[pages.length - 1].incarnation;
  for (const page of pages) {
    if (page.snapshot_required) return { state: prev, outcome: 'resnapshot' };
    if (page.incarnation !== incarnation) return { state: prev, outcome: 'resnapshot' };
    if (mode === 'changes' && page.incarnation !== prev.incarnation) return { state: prev, outcome: 'resnapshot' };
  }
  const last = pages[pages.length - 1];
  if (!isFinal(last)) return { state: prev, outcome: 'incomplete' };
  const items = mode === 'snapshot' ? new Map() : new Map(prev.items);
  for (const page of pages) {
    for (const raw of page.items || []) {
      const item = normaliseItem(raw);
      if (item) items.set(item.device_key, item);
    }
    for (const key of page.removed || []) items.delete(key);
  }
  return { state: { items, watermark: last.high_watermark, incarnation }, outcome: 'ok' };
};

// ---- Display ----

const ON_OFF = Object.freeze({
  motion: ['Motion', 'Clear'],
  contact: ['Open', 'Closed'],
  occupancy: ['Occupied', 'Vacant'],
  binary_sensor: ['On', 'Off'],
  light: ['On', 'Off'],
  switch: ['On', 'Off'],
});

const ROOM_STATE_WORDS = Object.freeze({ setup: 'Setup', in_use: 'In Use', closing_down: 'Closing Down', off: 'Not used' });

const withUnit = (value, unit) => `${value}${unit ? ` ${unit}` : ''}`;

const asOnOff = (value) => {
  if (value === true || value === 'on') return true;
  if (value === false || value === 'off') return false;
  return null;
};

export const displayValue = (item) => {
  if (item.state === 'not_connected') return NOT_CONNECTED_WORD;
  if (item.state === 'unavailable') return UNAVAILABLE_WORD;
  const { kind, value, unit } = item;
  if (value === null || value === undefined) return kind === 'light' || kind === 'switch' ? 'Unknown' : NO_VALUE_WORD;
  if (ON_OFF[kind]) {
    const on = asOnOff(value);
    if (on !== null) return ON_OFF[kind][on ? 0 : 1];
  }
  if (kind === 'room_state' && typeof value === 'string') return ROOM_STATE_WORDS[value] || value;
  // Numbers (temperature, humidity, target) and everything else, including unknown kinds: the raw value, with its unit.
  return withUnit(String(value), unit);
};

const groupIndex = (kind) => GROUP_ORDER.indexOf(normaliseKind(kind));

export const sortItems = (items) => [...items].sort((a, b) => (
  groupIndex(a.kind) - groupIndex(b.kind)
  || a.label.localeCompare(b.label, 'en', { sensitivity: 'base' })
  || (a.device_key < b.device_key ? -1 : a.device_key > b.device_key ? 1 : 0)
));

// Display rows. device_key is the React key only and is never part of the row text.
export const displayRows = (state) => sortItems([...state.items.values()]).map((item) => ({
  key: item.device_key,
  label: item.label || 'Device',
  kind: item.kind,
  text: displayValue(item),
  state: item.state,
  observed_at: item.state === 'not_connected' ? null : item.observed_at,
}));

// ---- Failure classification ----

const bodyCode = (cause) => {
  const error = cause?.response?.data?.error;
  if (typeof error === 'string') return error;
  return (error && typeof error.code === 'string' && error.code) || '';
};

// Returns 'fallback' | 'not_linked' | 'unreachable' | 'not_mapped' | 'mismatch' | 'generic'. Discriminates on the body error code;
// a 409 or 202 without a LIST_* / CSLPT_* code is Core's own answer (ROOMLIST-RULE-010).
export const classifyListFailure = (cause) => {
  if (cause?.listFailure === 'mismatch') return 'mismatch';
  if (cause?.listFailure === 'pending') return 'unreachable';
  const code = bodyCode(cause);
  if (cause?.response?.status === 403 && code === 'BOT_KEY_ROUTE_FORBIDDEN') return 'permission_denied';
  if (cause?.response?.status === 404 && code === 'NOT_FOUND') return 'fallback';
  if (code === 'ROOM_NOT_LINKED') return 'not_linked';
  if (code === 'LIST_ROOM_NOT_MAPPED') return 'not_mapped';
  if (code === 'CSLPT_TARGET_UNAVAILABLE' || code === 'TUNNEL_NODE_TIMEOUT') return 'unreachable';
  return 'generic';
};
